// Função auxiliar para formatar telefone ao exibir na UI
// Adicione esta função ao seu arquivo de utilidades ou nos componentes relevantes
// Por exemplo: src/utils/formatters.js

/**
 * Formata um número de telefone no formato internacional para exibição amigável na UI
 * Converte +5582991050175 para (55) 82991-0501
 */

export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';

  // Verifica se é um número internacional começando com +
  if (phoneNumber.startsWith('+')) {
    // Assume o formato padrão do Brasil: +55DDNNNNNNNNN
    try {
      // Remove o + e separa o código do país (assume 2 dígitos para Brasil)
      const countryCode = phoneNumber.substring(1, 3);
      
      // Pega o DDD (2 dígitos)
      const areaCode = phoneNumber.substring(3, 5);
      
      // Parte principal do número - verifica o tamanho para determinar o formato
      if (phoneNumber.length === 13) { // +55 + DDD (2) + número (8)
        const firstPart = phoneNumber.substring(5, 9);
        const secondPart = phoneNumber.substring(9);
        return `(${countryCode}) ${areaCode}${firstPart}-${secondPart}`;
      } else if (phoneNumber.length === 14) { // +55 + DDD (2) + número com 9 na frente (9)
        const firstPart = phoneNumber.substring(5, 10);
        const secondPart = phoneNumber.substring(10);
        return `(${countryCode}) ${areaCode}${firstPart}-${secondPart}`;
      } else {
        // Para outros tamanhos, tenta dividir na metade
        const remainder = phoneNumber.substring(5);
        const mid = Math.floor(remainder.length / 2);
        const firstPart = remainder.substring(0, mid);
        const secondPart = remainder.substring(mid);
        return `(${countryCode}) ${areaCode}${firstPart}-${secondPart}`;
      }
    } catch (e) {
      // Em caso de erro no parsing, retorna o original
      console.error("Erro ao formatar telefone:", e);
      return phoneNumber;
    }
  }

  // Se já estiver em formato nacional sem o +
  if (phoneNumber.length === 10 || phoneNumber.length === 11) {
    const areaCode = phoneNumber.substring(0, 2);
    if (phoneNumber.length === 10) { // DDD + 8 dígitos
      const firstPart = phoneNumber.substring(2, 6);
      const secondPart = phoneNumber.substring(6);
      return `(${areaCode}) ${firstPart}-${secondPart}`;
    } else { // DDD + 9 dígitos (com 9 na frente para celular)
      const firstPart = phoneNumber.substring(2, 7);
      const secondPart = phoneNumber.substring(7);
      return `(${areaCode}) ${firstPart}-${secondPart}`;
    }
  }

  // Para outros formatos, retorna como está
  return phoneNumber;
};

/**
 * Converte o formato amigável de telefone para o formato internacional
 * Converte (55) 82991-0501 para +5582991050175
 */
export const normalizePhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';

  // Remove todos os caracteres não numéricos
  const numbersOnly = phoneNumber.replace(/\D/g, '');

  // Verifica se o número já inclui código do país (assumindo Brasil, 55)
  if (numbersOnly.startsWith('55') && numbersOnly.length >= 12) {
    return `+${numbersOnly}`;
  }

  // Caso tenha apenas DDD + número (sem código do país), adiciona o +55
  if (numbersOnly.length === 10 || numbersOnly.length === 11) {
    return `+55${numbersOnly}`;
  }

  // Retorna com + se for internacional, ou como está se for outro formato
  return numbersOnly.length > 10 ? `+${numbersOnly}` : numbersOnly;
};

/**
 * Função para padronizar o formato de armazenamento de números de telefone
 * no banco de dados. Garante que todos os telefones seguem o mesmo padrão.
 */
export const standardizePhoneForStorage = (phoneNumber) => {
  // Se não há telefone, retorna vazio
  if (!phoneNumber) return '';
  
  // Se já está no formato internacional, normaliza para garantir consistência
  if (phoneNumber.startsWith('+')) {
    return normalizePhoneNumber(phoneNumber);
  }
  
  // Para números sem o código internacional, adiciona o código do Brasil (+55)
  const numbersOnly = phoneNumber.replace(/\D/g, '');
  
  // Se o número já tem o código do país sem o +, adiciona o +
  if (numbersOnly.startsWith('55') && (numbersOnly.length >= 12)) {
    return `+${numbersOnly}`;
  }
  
  // Para números só com DDD e número, adiciona +55
  return `+55${numbersOnly}`;
};
  
  // No ProfilePage.jsx e ProfileModal.jsx, use estas funções para exibição e normalização
  // Exemplo de uso:
  
  // Ao exibir o telefone na UI:
  //const displayPhone = formatPhoneNumber(profile.phone);
  
  // Ao enviar o telefone para o backend:
  //const normalizedPhone = normalizePhoneNumber(formData.phone);